import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Box,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip as MuiTooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const CostosAgrupadosBar = ({ selectedIndustries = [], onBarClick = () => {} }) => {
  const [infoOpen, setInfoOpen] = useState(false);

  const allLabels = ['Servicios logísticos', 'Automotriz', 'Manufactura', 'Alimentos', 'Construcción', 'Salud/Farmacéutica', 'Retail'];

  const costData2022 = {
    'Servicios logísticos': { transporte: 19, distribución: 22, logístico: 24 },
    'Automotriz': { transporte: 7, distribución: 10, logístico: 11 },
    'Manufactura': { transporte: 4, distribución: 6, logístico: 9 },
    'Alimentos': { transporte: 5, distribución: 6, logístico: 9 },
    'Construcción': { transporte: 5, distribución: 6, logístico: 7 },
    'Salud/Farmacéutica': { transporte: 4, distribución: 5, logístico: 8 },
    'Retail': { transporte: 3, distribución: 4, logístico: 6 },
  };

  const costData2023 = {
    'Servicios logísticos': { transporte: 13, distribución: 15, logístico: 17 },
    'Automotriz': { transporte: 2, distribución: 14, logístico: 15 },
    'Manufactura': { transporte: 3, distribución: 4, logístico: 5 },
    'Alimentos': { transporte: 9, distribución: 15, logístico: 13 },
    'Construcción': { transporte: 11, distribución: 11, logístico: 11 },
    'Salud/Farmacéutica': { transporte: 8, distribución: 3, logístico: 12 },
    'Retail': { transporte: 4, distribución: 15, logístico: 11 },
  };

  const getDataValues = (data, key) =>
    (selectedIndustries.length > 0 ? selectedIndustries : allLabels).map((label) => data[label][key]);

  const chartData2022 = {
    labels: selectedIndustries.length > 0 ? selectedIndustries : allLabels,
    datasets: [
      {
        label: 'Costo de transporte',
        data: getDataValues(costData2022, 'transporte'),
        backgroundColor: '#A0C1E2',
      },
      {
        label: 'Costo de distribución',
        data: getDataValues(costData2022, 'distribución'),
        backgroundColor: '#3B5998',
      },
      {
        label: 'Costo total de servir',
        data: getDataValues(costData2022, 'logístico'),
        backgroundColor: '#6D6D6D',
      },
    ],
  };

  const chartData2023 = {
    labels: selectedIndustries.length > 0 ? selectedIndustries : allLabels,
    datasets: [
      {
        label: 'Costo de transporte',
        data: getDataValues(costData2023, 'transporte'),
        backgroundColor: '#76A9D3',
      },
      {
        label: 'Costo de distribución',
        data: getDataValues(costData2023, 'distribución'),
        backgroundColor: '#2A4771',
      },
      {
        label: 'Costo total de servir',
        data: getDataValues(costData2023, 'logístico'),
        backgroundColor: '#4F4F4F',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw}%`,
        },
      },
      datalabels: {
        color: '#000',
        font: {
          weight: 'bold',
          size: 9,
        },
        anchor: 'end',
        align: 'top',
        formatter: (value) => `${value}%`,
      },
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Industria',
        },
      },
      y: {
        beginAtZero: true,
        max: 30,
        ticks: {
          stepSize: 5,
          callback: (value) => `${value}%`,
        },
        title: {
          display: true,
          text: 'Porcentaje (%)',
        },
      },
    },
  };

  const handleBarClick = (industry) => {
    let updatedSelected = [...selectedIndustries];
    if (updatedSelected.includes(industry)) {
      updatedSelected = updatedSelected.filter((i) => i !== industry);
    } else {
      updatedSelected.push(industry);
    }
    onBarClick(updatedSelected);
  };

  const infoContent = {
    Analisis: 'El costo logístico total como proporción de las ventas en dos periodos consecutivos...',
    formula: 'Promedio de los costos',
    description: 'Los datos evidencian que el costo logístico total sigue siendo un reto relevante...',
  };

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MuiTooltip title="Información de la gráfica">
          <IconButton onClick={() => setInfoOpen(true)}>
            <InfoIcon />
          </IconButton>
        </MuiTooltip>
      </Box>

      <Box display="flex" justifyContent="center" flexWrap="wrap" mb={2}>
        {allLabels.map((industry) => (
          <FormControlLabel
            key={industry}
            control={
              <Checkbox
                checked={selectedIndustries.includes(industry)}
                onChange={() => handleBarClick(industry)}
                color="primary"
              />
            }
            label={industry}
          />
        ))}
      </Box>

      <Box mb={4}>
        <Typography variant="h6" align="center" gutterBottom>
          2022
        </Typography>
        <div style={{ height: '300px' }}>
          <Bar data={chartData2022} options={options} />
        </div>
      </Box>

      <Box>
        <Typography variant="h6" align="center" gutterBottom>
          2023
        </Typography>
        <div style={{ height: '300px' }}>
          <Bar data={chartData2023} options={options} />
        </div>
      </Box>

      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogTitle>Información de la Gráfica</DialogTitle>
        <DialogContent>
          <p><strong>Análisis:</strong> {infoContent.Analisis}</p>
          <p><strong>Fórmula:</strong> {infoContent.formula}</p>
          <p><strong>Descripción:</strong> {infoContent.description}</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} color="primary">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CostosAgrupadosBar;
